// Helpers fetch para backend.
//
// Dev: fetch a "/api/..." path relativo → el proxy de Vite
// (vite.config.ts) redirige a http://localhost:8787 (worker local).
//
// Prod (CF Pages): fetch a "/api/..." path relativo → cae en una
// Pages Function (apps/web/functions/api/[[path]].ts) que proxea al
// worker via Service Binding interno de CF. Same-origin → cero CORS.
//
// Historia (2026-06-01): probamos antes 2 approaches que fallaron:
//   1) _redirects con rewrite /api/* → URL del worker (CF Pages no
//      proxea rewrites externos a workers.dev, sirve el SPA fallback).
//   2) URL absoluta del worker via VITE_API_URL + CORS (cross-domain
//      con CF Access falla: el fetch que hace CF Access redirect-a-login
//      rompe el CORS preflight porque cloudflareaccess.com no permite
//      el origen del frontend).
// La solución correcta para CF + Access es Pages Functions como proxy.

import type {
  CampoCliente,
  AppState,
  PlayRequest,
  PlayResponse,
  MeResponse,
  LogoSize,
  LogoPosition,
} from "@juegossc/shared";
import { getAppToken, clearAppToken } from "./app-storage";
import { getDevAdminEmail } from "./admin-storage";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type FetchOpts = RequestInit & {
  app?: boolean;
  admin?: boolean;
  /** Si es true, devuelve la Response cruda (para downloads de CSV). */
  raw?: boolean;
  /** Timeout en ms — si la request tarda más, aborta con ApiError
   *  "El server tardó demasiado". Por default no hay timeout (el
   *  default del browser puede ser minutos), pero para flujos de juego
   *  (play, memoria-start, memoria-win) usamos 10s para que el cliente
   *  no quede esperando para siempre con red rota. */
  timeoutMs?: number;
};

async function apiCall<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body) {
    headers.set("Content-Type", "application/json");
  }

  if (opts.app) {
    const token = getAppToken();
    if (token) headers.set("X-App-Token", token);
  }

  if (opts.admin && import.meta.env.DEV) {
    // En dev, override del email (CF Access no está en local).
    const email = getDevAdminEmail();
    if (email) headers.set("X-User-Email", email);
  }

  // Setup del timeout opcional. Si timeoutMs viene, creamos un
  // AbortController propio que aborta al vencer. Si el caller ya pasó
  // signal externo (típicamente un controller atado al unmount del
  // componente), lo combinamos manualmente: cualquiera de los dos
  // dispara nuestro abort. Sin AbortSignal.any() porque no asumimos
  // soporte universal del browser del kiosko.
  let timeoutId: number | undefined;
  let signal = opts.signal;
  // Distinguir NUESTRO timeout de una cancelación intencional del caller
  // (signal externo): si abortó el caller, NO es un "tardó demasiado" y no
  // hay que mostrarlo como error de red.
  let timedOut = false;
  if (opts.timeoutMs) {
    const controller = new AbortController();
    timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, opts.timeoutMs);
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    signal = controller.signal;
  }

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...opts, headers, signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      // Solo es "tardó demasiado" si lo abortó NUESTRO timeout. Si abortó el
      // caller (signal externo, p.ej. cleanup de un effect), re-lanzamos el
      // AbortError limpio para que el caller lo distinga y no lo muestre como
      // un error de red al usuario.
      if (timedOut) {
        throw new ApiError(
          "El servidor tardó demasiado en responder. Probá de nuevo o verificá tu conexión.",
          0,
        );
      }
      throw e;
    }
    // Network error (sin AbortError) = red caída, DNS, CORS, etc.
    if (e instanceof TypeError) {
      throw new ApiError(
        "No se pudo conectar con el servidor. Verificá tu conexión.",
        0,
      );
    }
    throw e;
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }

  if (opts.raw) {
    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText);
      throw new ApiError(txt, res.status);
    }
    return res as unknown as T;
  }

  const isJson = res.headers.get("Content-Type")?.includes("application/json");
  const body = isJson
    ? await res.json().catch(() => ({ error: res.statusText }))
    : await res.text();

  if (!res.ok) {
    const msg =
      typeof body === "object" && body && "error" in body
        ? (body as { error: string }).error
        : `HTTP ${res.status}`;
    if (res.status === 401 && opts.app) {
      clearAppToken();
    }
    throw new ApiError(msg, res.status);
  }

  return body as T;
}

// Timeout estándar para fetches del flujo de juego (play, memoria
// start/win). 10s cubre cold start del Worker (~500ms-2s) + latencia
// alta de la red (~3-5s) con margen. Si tarda más, casi seguro hay
// algo roto — mejor mostrar error claro al cliente.
const GAME_FETCH_TIMEOUT_MS = 10_000;

// ──────────────────────────────────────────────
// Pantalla
// ──────────────────────────────────────────────
export type SucursalPublic = {
  id: number;
  nombre: string;
  slug: string;
};

export async function appListSucursales(): Promise<SucursalPublic[]> {
  return apiCall("/app/sucursales", { timeoutMs: GAME_FETCH_TIMEOUT_MS });
}

export async function appLogin(
  sucursalId: number,
  pin: string,
): Promise<{ token: string; expires_at: number }> {
  return apiCall("/app/login", {
    method: "POST",
    body: JSON.stringify({ sucursal_id: sucursalId, pin }),
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
  });
}

export async function appState(): Promise<AppState> {
  // Timeout obligatorio: appState dispara el mount de TODAS las pantallas
  // kiosko. Sin timeout, si la red cae al cargar Landing, queda en
  // loading infinito.
  return apiCall("/app/state", { app: true, timeoutMs: GAME_FETCH_TIMEOUT_MS });
}

export async function appPlay(
  req: PlayRequest,
): Promise<PlayResponse & { modo_prueba: boolean }> {
  return apiCall("/app/play", {
    method: "POST",
    body: JSON.stringify(req),
    app: true,
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
  });
}

export async function appPlayCustomer(
  playId: number,
  customer: PlayRequest["customer"],
): Promise<{ ok: boolean }> {
  return apiCall(`/app/play/${playId}/customer`, {
    method: "PATCH",
    body: JSON.stringify(customer ?? {}),
    app: true,
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
  });
}

// ──────────────────────────────────────────────
// Admin
// ──────────────────────────────────────────────
function asSucursalQuery(asSucursal?: number): string {
  return asSucursal !== undefined ? `?as_sucursal=${asSucursal}` : "";
}

export async function adminMe(): Promise<MeResponse> {
  return apiCall("/admin/me", { admin: true });
}

export type AdminMetrics = {
  window_days: number;
  since: number;
  total: number;
  entregados: number;
  consolation: number;
};

export async function adminMetrics(
  days: number,
  asSucursal?: number,
): Promise<AdminMetrics> {
  const q = asSucursal !== undefined
    ? `?days=${days}&as_sucursal=${asSucursal}`
    : `?days=${days}`;
  return apiCall(`/admin/metrics${q}`, { admin: true });
}

export type AdminGrupo = {
  id: number;
  sucursal_id: number;
  juego_type: "ruleta" | "raspadita";
  nombre: string;
  created_at: number;
};

export async function adminListGrupos(asSucursal?: number): Promise<AdminGrupo[]> {
  return apiCall(`/admin/grupos${asSucursalQuery(asSucursal)}`, { admin: true });
}

// ──────────────────────────────────────────────
// Productos (modelo unificado — migrations 016 y 020)
// ──────────────────────────────────────────────
// Reemplaza a AdminPremio/adminListPremios etc. El producto vive 1
// vez en DB con stock único; el juego es una relación M:N.

export type GameTypeAll = "ruleta" | "raspadita" | "memoria";
export type ProductoNivel = "facil" | "medio" | "dificil";

export type ProductoJuego = {
  juego_type: GameTypeAll;
  peso: number;
  sort_order: number;
};

export type AdminProducto = {
  id: number;
  sucursal_id: number;
  nombre: string;
  color: string | null;
  is_consolation: number; // 0|1 (server-side flags)
  is_reintento: number;
  stock: number | null;
  activo: number;
  nivel: ProductoNivel | null;
  created_at: number;
  updated_at: number;
  // Juegos donde el producto participa. Vacío = el producto existe
  // pero no aparece en ningún juego (estado válido).
  juegos: ProductoJuego[];
};

export async function adminListProductos(
  asSucursal?: number,
): Promise<AdminProducto[]> {
  return apiCall(`/admin/productos${asSucursalQuery(asSucursal)}`, {
    admin: true,
  });
}

export type CreateProductoBody = {
  nombre: string;
  color?: string | null;
  is_consolation?: boolean;
  is_reintento?: boolean;
  activo?: boolean;
  stock?: number | null;
  nivel?: ProductoNivel | null;
  juegos?: Array<{
    juego_type: GameTypeAll;
    peso?: number;
    sort_order?: number;
  }>;
};

export async function adminCreateProducto(
  body: CreateProductoBody,
  asSucursal?: number,
): Promise<{ ok: boolean; id: number }> {
  return apiCall(`/admin/productos${asSucursalQuery(asSucursal)}`, {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

// Update parcial. Si vienen juegos, REEMPLAZA completo el array. Si
// se omite, los juegos no se tocan.
export type UpdateProductoBody = Partial<CreateProductoBody>;

export async function adminUpdateProducto(
  id: number,
  body: UpdateProductoBody,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/productos/${id}${asSucursalQuery(asSucursal)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function adminDeleteProducto(
  id: number,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/productos/${id}${asSucursalQuery(asSucursal)}`, {
    method: "DELETE",
    admin: true,
  });
}

// Reorder dentro de un juego (no afecta otros juegos del producto).
export async function adminReorderProductos(
  juegoType: GameTypeAll,
  orderedIds: number[],
  asSucursal?: number,
): Promise<{ ok: boolean; changes: number }> {
  return apiCall(`/admin/productos/reorder${asSucursalQuery(asSucursal)}`, {
    method: "POST",
    body: JSON.stringify({ juego_type: juegoType, ordered_ids: orderedIds }),
    admin: true,
  });
}

// Siembra una lista curada de productos típicos en la sucursal del
// caller. Idempotente: los nombres ya existentes (case-insensitive) se
// saltean. La lista vive en el backend — no enviamos productos desde
// el cliente (no son data del user, son defaults del sistema).
export async function adminSeedExampleProductos(
  asSucursal?: number,
): Promise<{ ok: boolean; created: number; skipped: number }> {
  return apiCall(
    `/admin/productos/seed-examples${asSucursalQuery(asSucursal)}`,
    {
      method: "POST",
      admin: true,
    },
  );
}

// Fusiona el producto src dentro de dst (mueve plays.prize_id +
// producto_por_juego con upsert, borra src). Ambos deben ser de la
// misma sucursal del caller.
export async function adminMergeProductos(
  srcId: number,
  dstId: number,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(
    `/admin/productos/${srcId}/merge-into/${dstId}${asSucursalQuery(asSucursal)}`,
    {
      method: "POST",
      admin: true,
    },
  );
}

export type AdminPlay = {
  id: number;
  sucursal_id: number;
  // memoria también registra plays (insertPlay gameType="memoria").
  game_type: "ruleta" | "raspadita" | "memoria";
  prize_id: number | null;
  prize_name_snapshot: string;
  is_consolation: number;
  customer_name: string | null;
  customer_phone: string | null;
  customer_doc: string | null;
  customer_email: string | null;
  customer_ticket: string | null;
  delivered: number;
  delivered_at: number | null;
  delivered_by_email: string | null;
  notes: string | null;
  created_at: number;
};

export type PlaysFilters = {
  desde?: number;
  hasta?: number;
  game_type?: "ruleta" | "raspadita" | "memoria";
  prize_id?: number;
  delivered?: boolean;
  is_consolation?: boolean;
  limit?: number;
  offset?: number;
};

function filtersToQuery(f: PlaysFilters): string {
  const params: string[] = [];
  if (f.desde !== undefined) params.push(`desde=${f.desde}`);
  if (f.hasta !== undefined) params.push(`hasta=${f.hasta}`);
  if (f.game_type) params.push(`game_type=${f.game_type}`);
  if (f.prize_id !== undefined) params.push(`prize_id=${f.prize_id}`);
  if (f.delivered !== undefined) params.push(`delivered=${f.delivered}`);
  if (f.is_consolation !== undefined) params.push(`is_consolation=${f.is_consolation}`);
  if (f.limit !== undefined) params.push(`limit=${f.limit}`);
  if (f.offset !== undefined) params.push(`offset=${f.offset}`);
  return params.length > 0 ? params.join("&") : "";
}

export async function adminListPlays(
  filters: PlaysFilters,
  asSucursal?: number,
): Promise<{ rows: AdminPlay[]; total: number }> {
  const fq = filtersToQuery(filters);
  const sq = asSucursal !== undefined ? `as_sucursal=${asSucursal}` : "";
  const q = [fq, sq].filter(Boolean).join("&");
  return apiCall(`/admin/plays${q ? "?" + q : ""}`, { admin: true });
}

export async function adminDeliverPlay(
  id: number,
  notes?: string,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/plays/${id}/deliver${asSucursalQuery(asSucursal)}`, {
    method: "PATCH",
    body: JSON.stringify({ notes }),
    admin: true,
  });
}

export type UpdatePlayBody = {
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_doc?: string | null;
  customer_email?: string | null;
  customer_ticket?: string | null;
  notes?: string | null;
};

export async function adminUpdatePlay(
  id: number,
  body: UpdatePlayBody,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/plays/${id}${asSucursalQuery(asSucursal)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function adminDeletePlay(
  id: number,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/plays/${id}${asSucursalQuery(asSucursal)}`, {
    method: "DELETE",
    admin: true,
  });
}

// Borra TODOS los plays de la sucursal en bulk. Requiere ?confirm=BORRAR
// explícito (validado en backend) — la UI le pide al admin que tipee
// "BORRAR" como confirmación final antes de llamar.
export async function adminDeleteAllPlays(
  asSucursal?: number,
): Promise<{ ok: boolean; deleted: number }> {
  const sq = asSucursal !== undefined ? `&as_sucursal=${asSucursal}` : "";
  return apiCall(`/admin/plays?confirm=BORRAR${sq}`, {
    method: "DELETE",
    admin: true,
  });
}

export async function adminExportPlaysCsv(
  filters: PlaysFilters,
  asSucursal?: number,
): Promise<void> {
  // Devuelve la response como blob y dispara descarga browser-side.
  const fq = filtersToQuery(filters);
  const sq = asSucursal !== undefined ? `as_sucursal=${asSucursal}` : "";
  const q = [fq, sq].filter(Boolean).join("&");
  const res = await apiCall<Response>(
    `/admin/plays/export${q ? "?" + q : ""}`,
    { admin: true, raw: true },
  );
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ganadores-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Migration 042: Mi pantalla (admin de sucursal) solo maneja lo operativo. El
// config de juego (038) y el sonido (041) son globales (super-admin).
export type AdminSettings = {
  sucursal_id: number;
  pin_configurado: boolean;
  registrar_datos_cliente: boolean;
  campos_cliente: CampoCliente[];
  campos_obligatorios: CampoCliente[];
  modo_prueba: boolean;
  modo_prueba_activado_at: number | null;
  auto_bloqueo_minutos: number;
  updated_at: number;
};

export type UpdateSettingsBody = {
  pin?: string;
  registrar_datos_cliente?: boolean;
  campos_cliente?: CampoCliente[];
  campos_obligatorios?: CampoCliente[];
  modo_prueba?: boolean;
  auto_bloqueo_minutos?: number;
};

// Migration 029: lista pública de estilos de sonido activos.
// La usa el selector del admin de sucursal en Mi pantalla (/admin/pantalla).
// Re-exportamos el tipo del shared package para que el frontend tenga
// una sola fuente de verdad y consumidores externos sigan importando
// desde "lib/api" como antes.
export type { SoundStylePublic } from "@juegossc/shared";
import type { SoundStylePublic } from "@juegossc/shared";

export async function adminListSoundStyles(): Promise<SoundStylePublic[]> {
  return apiCall("/admin/sound-styles", { admin: true });
}

// CRUD super_admin de sound_styles (migration 029).
export type SuperSoundStyleRow = {
  id: number;
  slug: string;
  label: string;
  description: string | null;
  activo: number;
  sort_order: number;
  created_at: number;
};

export async function superListSoundStyles(): Promise<SuperSoundStyleRow[]> {
  return apiCall("/super/sound-styles", { admin: true });
}

export async function superCreateSoundStyle(body: {
  slug: string;
  label: string;
  description?: string | null;
  activo?: boolean;
  sort_order?: number;
}): Promise<{ ok: boolean; id: number }> {
  return apiCall("/super/sound-styles", {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superUpdateSoundStyle(
  id: number,
  body: {
    label?: string;
    description?: string | null;
    activo?: boolean;
    sort_order?: number;
  },
): Promise<{ ok: boolean }> {
  return apiCall(`/super/sound-styles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superDeleteSoundStyle(
  id: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/super/sound-styles/${id}`, {
    method: "DELETE",
    admin: true,
  });
}

export async function adminGetSettings(
  asSucursal?: number,
): Promise<AdminSettings> {
  return apiCall(`/admin/settings${asSucursalQuery(asSucursal)}`, {
    admin: true,
  });
}

export async function adminUpdateSettings(
  body: UpdateSettingsBody,
  asSucursal?: number,
): Promise<{ ok: boolean }> {
  return apiCall(`/admin/settings${asSucursalQuery(asSucursal)}`, {
    method: "PUT",
    body: JSON.stringify(body),
    admin: true,
  });
}

// ──────────────────────────────────────────────
// Super admin
// ──────────────────────────────────────────────
export type SucursalRow = {
  id: number;
  nombre: string;
  slug: string;
  ciudad: string | null;
  activa: number;
  created_at: number;
};

export async function superListSucursales(): Promise<SucursalRow[]> {
  return apiCall("/super/sucursales", { admin: true });
}

export async function superCreateSucursal(body: {
  nombre: string;
  slug: string;
  ciudad?: string;
}): Promise<{ ok: boolean; id: number }> {
  return apiCall("/super/sucursales", {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superUpdateSucursal(
  id: number,
  body: Partial<{ nombre: string; slug: string; ciudad: string | null; activa: boolean }>,
): Promise<{ ok: boolean }> {
  return apiCall(`/super/sucursales/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    admin: true,
  });
}

export type UserRow = {
  email: string;
  role: "super_admin" | "admin";
  sucursal_id: number | null;
  created_at: number;
  updated_at: number;
};

export async function superListUsers(): Promise<UserRow[]> {
  return apiCall("/super/users", { admin: true });
}

export async function superCreateUser(body: {
  email: string;
  role: "super_admin" | "admin";
  sucursal_id?: number | null;
}): Promise<{ ok: boolean }> {
  return apiCall("/super/users", {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superDeleteUser(email: string): Promise<{ ok: boolean }> {
  return apiCall(`/super/users/${encodeURIComponent(email)}`, {
    method: "DELETE",
    admin: true,
  });
}

export type CampanaRow = {
  id: number;
  nombre_interno: string;
  nombre_display: string;
  imagen_url: string;
  activa: number;
  fecha_inicio: number | null;
  fecha_fin: number | null;
  created_at: number;
};

export async function superListCampanas(): Promise<CampanaRow[]> {
  return apiCall("/super/campanas", { admin: true });
}

export async function superCreateCampana(body: {
  nombre_interno: string;
  nombre_display: string;
  imagen_url: string;
  fecha_inicio?: number;
  fecha_fin?: number;
}): Promise<{ ok: boolean; id: number }> {
  return apiCall("/super/campanas", {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superActivateCampana(id: number): Promise<{ ok: boolean }> {
  return apiCall(`/super/campanas/${id}/activate`, {
    method: "POST",
    admin: true,
  });
}

export async function superDeactivateAllCampanas(): Promise<{ ok: boolean }> {
  return apiCall("/super/campanas/deactivate", { method: "POST", admin: true });
}

export async function superDeleteCampana(id: number): Promise<{ ok: boolean }> {
  return apiCall(`/super/campanas/${id}`, { method: "DELETE", admin: true });
}

export type GlobalSettings = {
  id: number;
  logo_url: string | null;
  bg_landscape_url: string | null;
  bg_portrait_url: string | null;
  color_primary: string;
  color_accent: string;
  campana_activa_id: number | null;
  // Migration 029: sound_enabled + sound_style se movieron a SucursalSettings
  // (cada sucursal tiene su propio). No están en este tipo a propósito.
  logo_size: LogoSize; // legacy, ya no se usa en frontend
  logo_width_px: number;
  logo_position: LogoPosition;
  campana_max_height_px: number;
  // Migration 034: alto de la campaña dentro del juego.
  campana_juego_height_px: number;
  // Migration 035: aire arriba de la campaña dentro del juego.
  campana_juego_top_margin_px: number;
  // Migration 037: posición vertical del cuerpo del juego (0..100%).
  juego_vertical_pos_pct: number;
  // Migration 039: tamaño del cuerpo de los juegos (memoria + raspadita).
  memoria_carta_px: number;
  raspadita_panel_w_px: number;
  raspadita_panel_h_px: number;
  // Migration 040: cap de DPR de los canvas de juego (nitidez).
  juego_canvas_dpr_max: number;
  // Migration 041: sonido global.
  sound_style_slug: string;
  sound_enabled: number;
  // Migration 044: tamaño de la rueda + aire arriba del logo (landing).
  ruleta_wheel_size_px: number;
  landing_logo_top_margin_px: number;
  // Migration 045: posición vertical fina del landing (0..100%).
  landing_vertical_pos_pct: number;
  // Migration 038: config de juegos movido a global (super-admin).
  ruleta_habilitada: number;
  ruleta_velocidad_minima: number;
  ruleta_variacion_aleatoria: number;
  ruleta_frenado: number;
  ruleta_umbral_casi_pasa: number;
  ruleta_color_borde: string;
  ruleta_color_centro: string;
  ruleta_grosor_divisoria: number;
  ruleta_text_align: "left" | "center" | "right";
  raspadita_habilitada: number;
  raspadita_mecanica: "panel" | "grid" | "elije";
  raspadita_grid_dinamica: "encontrar" | "intentos" | "todas";
  memoria_habilitada: number;
  memoria_seg_mem_facil: number;
  memoria_seg_mem_medio: number;
  memoria_seg_mem_dificil: number;
  memoria_tiempo_facil: number;
  memoria_tiempo_medio: number;
  memoria_tiempo_dificil: number;
  // Migration 013/033: íconos custom para los botones de juego.
  icon_ruleta_url: string | null;
  icon_raspadita_url: string | null;
  icon_memoria_url: string | null;
  // Migration 027: tamaño de los botones de juego en la landing.
  landing_icon_size_px: number;
  // Migration 026: ruleta_color_borde/centro/grosor_divisoria/
  // text_align pasaron a sucursal_settings (cada sucursal personaliza
  // su ruleta).
  // Migration 018: tipografías configurables.
  font_heading: string;
  font_body: string;
  font_display: string;
  // Migration 022: 6 imágenes del juego memoria.
  memoria_imagen_1_url: string | null;
  memoria_imagen_2_url: string | null;
  memoria_imagen_3_url: string | null;
  memoria_imagen_4_url: string | null;
  memoria_imagen_5_url: string | null;
  memoria_imagen_6_url: string | null;
  // Migration 031: layout configurable del landing.
  landing_campana_to_iconos_gap_px: number;
  landing_iconos_vertical_align: "top" | "center" | "bottom";
  landing_iconos_horizontal_align: "left" | "center" | "right";
  landing_campana_top_margin_px: number;
  updated_at: number;
};

export async function superGetGlobalSettings(): Promise<GlobalSettings> {
  return apiCall("/super/global-settings", { admin: true });
}

export async function superUpdateGlobalSettings(body: {
  logo_url?: string | null;
  bg_landscape_url?: string | null;
  bg_portrait_url?: string | null;
  color_primary?: string;
  color_accent?: string;
  // Migration 029: sound_enabled + sound_style se actualizan ahora vía
  // adminUpdateSettings (por sucursal). No los aceptamos acá.
  logo_size?: LogoSize;
  logo_width_px?: number;
  logo_position?: LogoPosition;
  campana_max_height_px?: number;
  // Migration 034: alto de la campaña dentro del juego.
  campana_juego_height_px?: number;
  // Migration 035: aire arriba de la campaña dentro del juego.
  campana_juego_top_margin_px?: number;
  // Migration 037: posición vertical del cuerpo del juego (0..100%).
  juego_vertical_pos_pct?: number;
  // Migration 039: tamaño del cuerpo de los juegos (memoria + raspadita).
  memoria_carta_px?: number;
  raspadita_panel_w_px?: number;
  raspadita_panel_h_px?: number;
  // Migration 040: cap de DPR de los canvas de juego (nitidez).
  juego_canvas_dpr_max?: number;
  // Migration 041: sonido global.
  sound_style_slug?: string;
  sound_enabled?: boolean;
  // Migration 044: tamaño de la rueda + aire arriba del logo (landing).
  ruleta_wheel_size_px?: number;
  landing_logo_top_margin_px?: number;
  // Migration 045: posición vertical fina del landing (0..100%).
  landing_vertical_pos_pct?: number;
  // Migration 038: config de juegos movido a global (super-admin).
  ruleta_habilitada?: boolean;
  ruleta_velocidad_minima?: number;
  ruleta_variacion_aleatoria?: number;
  ruleta_frenado?: number;
  ruleta_umbral_casi_pasa?: number;
  ruleta_color_borde?: string;
  ruleta_color_centro?: string;
  ruleta_grosor_divisoria?: number;
  ruleta_text_align?: "left" | "center" | "right";
  raspadita_habilitada?: boolean;
  raspadita_mecanica?: "panel" | "grid" | "elije";
  raspadita_grid_dinamica?: "encontrar" | "intentos" | "todas";
  memoria_habilitada?: boolean;
  memoria_seg_mem_facil?: number;
  memoria_seg_mem_medio?: number;
  memoria_seg_mem_dificil?: number;
  memoria_tiempo_facil?: number;
  memoria_tiempo_medio?: number;
  memoria_tiempo_dificil?: number;
  // Migration 013/033: íconos custom para los botones de juego.
  // null explícito = volver al fallback Lucide.
  icon_ruleta_url?: string | null;
  icon_raspadita_url?: string | null;
  icon_memoria_url?: string | null;
  // Migration 027: tamaño de los botones de juego en la landing.
  landing_icon_size_px?: number;
  // Migration 026: ruleta_color_borde/centro/grosor_divisoria/
  // text_align se actualizan ahora vía adminUpdateSettings (por sucursal).
  // Migration 018: tipografías. Strings de la lista curada (lib/fonts).
  font_heading?: string;
  font_body?: string;
  font_display?: string;
  // Migration 022: imágenes del juego memoria. null = quitar slot.
  memoria_imagen_1_url?: string | null;
  memoria_imagen_2_url?: string | null;
  memoria_imagen_3_url?: string | null;
  memoria_imagen_4_url?: string | null;
  memoria_imagen_5_url?: string | null;
  memoria_imagen_6_url?: string | null;
  // Migration 031: layout del landing.
  landing_campana_to_iconos_gap_px?: number;
  landing_iconos_vertical_align?: "top" | "center" | "bottom";
  landing_iconos_horizontal_align?: "left" | "center" | "right";
  landing_campana_top_margin_px?: number;
}): Promise<{ ok: boolean }> {
  return apiCall("/super/global-settings", {
    method: "PUT",
    body: JSON.stringify(body),
    admin: true,
  });
}

// ──────────────────────────────────────────────
// Sound assets (migration 017)
// ──────────────────────────────────────────────
export type SoundAsset = {
  slot_key: string;
  url: string;
  uploaded_at: number;
};

export async function superListSounds(): Promise<SoundAsset[]> {
  return apiCall("/super/sounds", { admin: true });
}

// Upload de audio para un slot. Multipart con file + slot_key.
// Retorna la url asignada. Si ya había sound en ese slot, se reemplaza.
export async function superUploadSound(
  file: File,
  slotKey: string,
): Promise<{ ok: boolean; url: string; slot_key: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("slot_key", slotKey);
  const headers = new Headers();
  if (import.meta.env.DEV) {
    const email = getDevAdminEmail();
    if (email) headers.set("X-User-Email", email);
  }
  const res = await fetch("/api/super/sounds", {
    method: "POST",
    body: form,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      (body as { error?: string }).error ?? `HTTP ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

export async function superDeleteSound(
  slotKey: string,
): Promise<{ ok: boolean }> {
  return apiCall(`/super/sounds/${slotKey}`, {
    method: "DELETE",
    admin: true,
  });
}

export type AssetKind =
  | "logo"
  | "bg-landscape"
  | "bg-portrait"
  | "campana"
  | "icon-ruleta"
  | "icon-raspadita"
  | "icon-memoria"
  // Migration 022: 6 slots de imágenes del juego memoria.
  | "memoria-1"
  | "memoria-2"
  | "memoria-3"
  | "memoria-4"
  | "memoria-5"
  | "memoria-6";

// Upload a R2 vía endpoint super.
// Devuelve la URL pública del asset subido.
export async function superUploadAsset(
  file: File,
  kind: AssetKind,
): Promise<{ ok: boolean; url: string; key: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  const headers = new Headers();
  if (import.meta.env.DEV) {
    const email = getDevAdminEmail();
    if (email) headers.set("X-User-Email", email);
  }
  const res = await fetch("/api/super/assets", {
    method: "POST",
    body: form,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      (body as { error?: string }).error ?? `HTTP ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

// ──────────────────────────────────────────────
// FAQ (migration 025) — sistema de ayuda admin
// ──────────────────────────────────────────────
// GET es accesible para cualquier admin (lo usa el modal de ayuda).
// Las mutaciones son super_admin only (las usa /admin/faqs).

export type FaqRow = {
  id: number;
  pregunta: string;
  respuesta: string;
  categoria: string;
  orden: number;
  created_at: number;
  updated_at: number;
};

export async function adminListFaqs(): Promise<FaqRow[]> {
  return apiCall("/admin/faqs", { admin: true });
}

export async function superCreateFaq(body: {
  pregunta: string;
  respuesta: string;
  categoria?: string;
  orden?: number;
}): Promise<{ ok: boolean; id: number }> {
  return apiCall("/super/faqs", {
    method: "POST",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superUpdateFaq(
  id: number,
  body: Partial<{
    pregunta: string;
    respuesta: string;
    categoria: string;
    orden: number;
  }>,
): Promise<{ ok: boolean }> {
  return apiCall(`/super/faqs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    admin: true,
  });
}

export async function superDeleteFaq(id: number): Promise<{ ok: boolean }> {
  return apiCall(`/super/faqs/${id}`, {
    method: "DELETE",
    admin: true,
  });
}

export async function superReorderFaqs(
  orderedIds: number[],
): Promise<{ ok: boolean; changes: number }> {
  return apiCall("/super/faqs/reorder", {
    method: "POST",
    body: JSON.stringify({ ordered_ids: orderedIds }),
    admin: true,
  });
}

// ──────────────────────────────────────────────
// Juego MEMORIA (migrations 022 + 023)
// ──────────────────────────────────────────────

export type MemoriaProductoElegible = {
  id: number;
  nombre: string;
  color: string | null;
  stock: number | null;
  nivel: "facil" | "medio" | "dificil";
};

export async function appMemoriaProductos(
  nivel: "facil" | "medio" | "dificil",
): Promise<MemoriaProductoElegible[]> {
  return apiCall(`/app/memoria/productos?nivel=${nivel}`, {
    app: true,
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
  });
}

export type MemoriaStartResponse = {
  ok: boolean;
  session_id: string;
  tiempo_segundos: number;
  seg_memorizar: number;
  imagenes: string[]; // 6 URLs
};

export async function appMemoriaStart(
  productoId: number,
  nivel: "facil" | "medio" | "dificil",
): Promise<MemoriaStartResponse> {
  return apiCall(`/app/memoria/start`, {
    method: "POST",
    body: JSON.stringify({ producto_id: productoId, nivel }),
    app: true,
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
  });
}

export type MemoriaWinResponse = {
  ok: boolean;
  play_id: number | null;
  prize: { id: number; nombre: string };
  stock_restante: number | null;
};

export async function appMemoriaWin(
  sessionId: string,
  signal?: AbortSignal,
): Promise<MemoriaWinResponse> {
  return apiCall(`/app/memoria/win`, {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId }),
    app: true,
    timeoutMs: GAME_FETCH_TIMEOUT_MS,
    signal,
  });
}
